import React, { useState, useEffect } from "react";
import * as style from "./Featuregroups.module.scss";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CustomBreadcrumb from "../../Components/CustomBreadcrumb/CustomBreadcrumb";
import { Tab, Tabs } from "@mui/material";
import {
  TextInput,
  TextArea,
  Toggle,
  LoadingSpinner,
} from "cprime-foundational-components";
import AddFeaturePanel from "./AddFeaturePanel";
import FeatureManage from "./FeatureManage";
import { useParams, useNavigate } from "react-router-dom";
import {
  editFeatureGroup,
  getFeatureGroupById,
  deleteFeatureGroup,
} from "../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../utils/toast";
import Button from "../../Components/Common/CprimeButton";

function EditFeatureGroup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [addFeaturePanelVisible, setAddFeaturePanelVisible] = useState(false);
  const [initialForm, setInitialForm] = useState({
    group_name: "",
    group_description: "",
    is_visible: false,
  });
  const [formValue, setFormValue] = useState({
    group_name: "",
    group_description: "",
    is_visible: false,
  });
  const [deleteButtonLoading, setDeleteButtonLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [triggerEffect, setTriggerEffect] = useState(false);
  const scssClass = style as any;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Open the panel when "Add Feature" button is clicked
  const handleAddFeaturePanelOpen = () => {
    setAddFeaturePanelVisible(true);
  };

  // Close the panel when "Cancel" button is clicked
  const handleAddFeaturePanelClose = () => {
    setAddFeaturePanelVisible(false);
  };

  const isFormValid = () => {
    return (
      (initialForm?.group_name === formValue?.group_name &&
        initialForm?.group_description === formValue?.group_description &&
        initialForm?.is_visible === formValue?.is_visible) ||
      !(formValue?.group_name && formValue?.group_description)
    );
  };

  const handleChange = (field: string, value: any) => {
    if (field) setFormValue({ ...formValue, [field]: value });
  };

  useEffect(() => {
    getDetailsById();
  }, []);

  const getDetailsById = async () => {
    try {
      setDetailsLoading(true);
      const res = await getFeatureGroupById(Number(id));
      if (res?.data) {
        setInitialForm({
          group_name: res?.data?.group_name || "",
          group_description: res?.data?.group_description || "",
          is_visible: res?.data?.is_visible || false,
        });
        setFormValue({
          group_name: res?.data?.group_name || "",
          group_description: res?.data?.group_description || "",
          is_visible: res?.data?.is_visible || false,
        });
      }
    } catch (error) {
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSaveFeature = async () => {
    const payload = {
      group_id: Number(id),
      ...formValue,
    };
    const response = await editFeatureGroup(payload);
    if (response?.error) {
      triggerToast(`${response?.error}`, "error");
    } else if (response?.status === "SUCCESS") {
      setInitialForm(formValue);
      triggerToast(`${response?.message}`, "success");
    }
    triggerToast(`${response?.message}`, response?.status?.toLowerCase());
  };

  const handleFeatureGroupDelete = async () => {
    const payload = {
      group_id: Number(id),
    };
    try {
      setDeleteButtonLoading(true);
      const response = await deleteFeatureGroup(payload);
      if (response?.error) {
        triggerToast(`${response?.error}`, "error");
      } else if (response?.status === "SUCCESS") {
        navigate("/feature-group");
        triggerToast(`${response?.message}`, "success");
      }
    } catch (err) {
    } finally {
      setDeleteButtonLoading(false);
    }
  };

  return (
    <>
      <div className={scssClass.editFeatureGroupContainer}>
        <div style={{ paddingBottom: "0.875rem" }}>
          <CustomBreadcrumb
            items={[
              {
                label: "Feature Groups",
                href: "/feature-group",
                icon: <HomeOutlinedIcon sx={{ color: "#2469FF" }} />,
              },
              {
                label: "Edit Feature Group",
                href: undefined,
              },
            ]}
          />
        </div>
        <div className={scssClass.editFeatureHeaderContainer}>
          <div
            style={{
              padding: "1rem 1rem 0rem",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxSizing: "border-box",
            }}
          >
            <p>{initialForm?.group_name}</p>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                variant="danger"
                composition="outline"
                size="medium"
                onClick={handleFeatureGroupDelete}
                loading={deleteButtonLoading}
              >
                Delete Feature Group
              </Button>
              {selectedTab === 1 && (
                <Button
                  size="medium"
                  variant="tertiary"
                  onClick={handleAddFeaturePanelOpen}
                >
                  Add Feature
                </Button>
              )}
            </div>
          </div>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiButtonBase-root": {
                color: "black !important",
                backgroundColor: "white",
              },
              "& .MuiTabs-indicator": {
                color: "black !important",
                backgroundColor: "black !important",
              },
              "& .MuiTabs-root": {
                marginBottom: "0px !important",
              },
            }}
          >
            <Tab
              label="Details"
              sx={{
                textTransform: "none",
                width: "120px",
                fontWeight: selectedTab === 0 ? "bold" : "",
              }}
            />
            <Tab
              label="Manage Features"
              sx={{
                textTransform: "none",
                width: "179px",
                fontWeight: selectedTab === 1 ? "bold" : "",
              }}
            />
          </Tabs>
        </div>
        {selectedTab === 0 && (
          <>
            {detailsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className={scssClass.editFeatureBodyContainer}>
                <div className={scssClass.editFeatureDetails}>
                  <TextInput
                    value={formValue?.group_name}
                    className={scssClass.textField}
                    onChange={(e: any) =>
                      handleChange("group_name", e.target.value)
                    }
                    placeholder="Enter Name"
                    label="Feature Group Name"
                  />
                  <TextArea
                    label="Description"
                    aria-label="minimum height"
                    minRows={3}
                    placeholder="Enter Description"
                    style={{ width: "99.5%" }}
                    value={formValue?.group_description}
                    onChange={(e) =>
                      handleChange("group_description", e.target.value)
                    }
                    maxLength={500}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#58595B",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Visible to Users
                    </p>
                    <Toggle
                      onChange={() =>
                        handleChange("is_visible", !formValue?.is_visible)
                      }
                      sx={{
                        overflow: "visible",
                        marginBottom: "4px",
                        "& .MuiSwitch-thumb": {
                          marginTop: "4px",
                        },
                      }}
                      checked={formValue?.is_visible}
                      toggledText={formValue?.is_visible ? "On" : "Off"}
                      size="small"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className={scssClass.editFeatureFooterContainer}>
              <Button
                variant="primary"
                // sx={{ margin: "0 10px" }}
                size="medium"
                onClick={handleSaveFeature}
                disabled={isFormValid()}
              >
                Save
              </Button>
            </div>
          </>
        )}
        {selectedTab === 1 && (
          <FeatureManage
            title="Features"
            openPanel={false}
            setOpenPanel={(value: boolean) => {}}
            triggerEffect={triggerEffect}
          />
        )}
      </div>

      {addFeaturePanelVisible && (
        <AddFeaturePanel
          onClose={handleAddFeaturePanelClose}
          open={addFeaturePanelVisible}
          triggerEffect={triggerEffect}
          setTriggerEffect={setTriggerEffect}
        />
      )}
    </>
  );
}

export default EditFeatureGroup;
